import { logger } from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Menu, LogOut, Home, Users, ClipboardCheck, ClipboardList, CalendarClock, BarChart3, Newspaper, Bot, Trash2, CheckCircle2, Mail } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const AdminLGPD = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sols, setSols] = useState([]);
  const [abertas, setAbertas] = useState(0);
  const [filtro, setFiltro] = useState('todas');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await axios.get(`${API}/admin/verify`, { withCredentials: true });
        localStorage.removeItem('admin_token');
        fetchData();
      } catch (error) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    };
    verifyAuth();
    // eslint-disable-next-line
  }, [navigate]);

  useEffect(() => { if (!loading) fetchData(); /* eslint-disable-next-line */ }, [filtro]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/lgpd/solicitacoes?status=${filtro}`, { withCredentials: true });
      setSols(res.data.solicitacoes || []);
      setAbertas(res.data.abertas || 0);
    } catch (error) {
      logger.error(error);
      toast({ title: 'Erro ao carregar', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const resolver = async (id) => {
    try {
      await axios.post(`${API}/lgpd/solicitacoes/${id}/resolver`, {}, { withCredentials: true });
      toast({ title: 'Marcada como atendida' });
      fetchData();
    } catch (error) { toast({ title: 'Erro', variant: 'destructive' }); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Remover esta solicitação?')) return;
    try {
      await axios.delete(`${API}/lgpd/solicitacoes/${id}`, { withCredentials: true });
      toast({ title: 'Solicitação removida' });
      fetchData();
    } catch (error) { toast({ title: 'Erro', variant: 'destructive' }); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/admin/logout`, {}, { withCredentials: true }); } catch (e) { /* ignore */ }
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const navItem = (path, Icon, label, active, testid) => (
    <button onClick={() => navigate(path)} data-testid={testid} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${active ? 'bg-yellow-500/20 text-yellow-400' : 'text-gray-400 hover:bg-gray-800'}`}>
      <Icon className="w-5 h-5" />{sidebarOpen && <span>{label}</span>}
    </button>
  );

  const fmt = (iso) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><div className="text-white text-xl">Carregando...</div></div>;
  }

  return (
    <div className="min-h-screen bg-black flex">
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 border-r border-gray-800 transition-all duration-300`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-8">
            {sidebarOpen && <h1 className="text-xl font-bold text-yellow-400">WING ADMIN</h1>}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white p-2"><Menu className="w-6 h-6" /></button>
          </div>
          <AdminNav active="lgpd" navigate={navigate} sidebarOpen={sidebarOpen} onLogout={handleLogout} />
        </div>
      </div>

      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Solicitações LGPD</h1>
            <p className="text-gray-400">{abertas} solicitação(ões) em aberto</p>
          </div>

          <div className="flex items-center gap-2 mb-4">
            {['todas', 'aberta', 'atendida'].map((f) => (
              <button key={f} onClick={() => setFiltro(f)} data-testid={`lgpd-filtro-${f}`} className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${filtro === f ? 'bg-yellow-500 text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                {f === 'todas' ? 'Todas' : f === 'aberta' ? 'Abertas' : 'Atendidas'}
              </button>
            ))}
          </div>

          <div className="glass-dark rounded-xl border border-white/10 overflow-hidden">
            {sols.length === 0 ? (
              <p className="text-gray-400 text-center py-12" data-testid="lgpd-empty">Nenhuma solicitação.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800 bg-white/5">
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Tipo</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Contato</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Mensagem</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Data</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Status</th>
                      <th className="text-center py-4 px-4 text-gray-400 font-medium text-sm">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sols.map((s) => (
                      <tr key={s.id} className="border-b border-gray-800 hover:bg-white/5 transition-colors" data-testid={`lgpd-row-${s.id}`}>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${s.tipo === 'exclusao' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>
                            {s.tipo === 'exclusao' ? 'Exclusão' : 'Acesso'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-gray-300 text-sm flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{s.contato}</td>
                        <td className="py-4 px-4 text-gray-300 text-sm max-w-[260px]">{s.mensagem || '-'}</td>
                        <td className="py-4 px-4 text-gray-400 text-sm">{fmt(s.created_at)}</td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${s.status === 'aberta' ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'}`}>
                            {s.status === 'aberta' ? 'Aberta' : 'Atendida'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center gap-1">
                            {s.status === 'aberta' && (
                              <button onClick={() => resolver(s.id)} data-testid={`lgpd-resolver-${s.id}`} className="p-2 text-green-400 hover:bg-green-500/10 rounded transition-colors" title="Marcar como atendida">
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => excluir(s.id)} data-testid={`lgpd-excluir-${s.id}`} className="p-2 text-red-400 hover:bg-red-500/10 rounded transition-colors" title="Remover">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLGPD;
